import { createContext, useContext, useState, useEffect } from "react";
import { SITE_CONFIG, STATS, AI_ASSISTANT_CONFIG } from "../data/siteData";

const SiteConfigContext = createContext(null);

// Defaults matching siteData.js — used when Firestore has no data
const DEFAULTS = {
  general: { ...SITE_CONFIG },
  styles: {
    colorBg: "#080810",
    colorText: "#D0D4DC",
    colorAccent: "#5CE0BE",
    colorAccentDark: "#45C4A3",
    colorAccentDeeper: "#3BA68A",
    colorAccentLight: "#A8F0DB",
    fontDisplay: "'Fraunces', 'Playfair Display', Georgia, serif",
    fontBody: "'Manrope', system-ui, sans-serif",
  },
  apis: {
    elevenlabsAgentId: AI_ASSISTANT_CONFIG.elevenlabs.agentId,
    elevenlabsEnabled: AI_ASSISTANT_CONFIG.elevenlabs.enabled !== false,
    heygenAvatarId: AI_ASSISTANT_CONFIG.heygen.avatarId,
    heygenEnabled: !!AI_ASSISTANT_CONFIG.heygen.enabled,
  },
  media: { avatarUrl: "", logoUrl: "", faviconUrl: "", ogImageUrl: "" },
  content: {
    heroHeadline: "IA aplicada. Data para decisiones. LATAM.",
    heroSubtitle:
      "Construyo agentes IA en producción. Chat IPT sirve a 100+ usuarios en Telefónica. Publico cada día lo que aprendo desde la trinchera enterprise.",
    stats: STATS,
  },
};

const API_URL = import.meta.env.VITE_API_URL || "";

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_URL}/api/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        // Merge remote over defaults per section
        const merged = {};
        for (const key of Object.keys(DEFAULTS)) {
          merged[key] = data[key]
            ? { ...DEFAULTS[key], ...data[key] }
            : DEFAULTS[key];
        }
        setConfig(merged);
      })
      .catch(() => {
        // Silently fall back to defaults
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Apply dynamic CSS variables when styles change
  useEffect(() => {
    const s = config.styles;
    const root = document.documentElement;
    root.style.setProperty("--color-bg", s.colorBg);
    root.style.setProperty("--color-text", s.colorText);
    root.style.setProperty("--color-accent", s.colorAccent);
    root.style.setProperty("--color-accent-dark", s.colorAccentDark);
    root.style.setProperty("--color-accent-deeper", s.colorAccentDeeper);
    root.style.setProperty("--color-accent-light", s.colorAccentLight);
    root.style.setProperty("--font-display", s.fontDisplay);
    root.style.setProperty("--font-body", s.fontBody);
  }, [config.styles]);

  return (
    <SiteConfigContext.Provider value={{ ...config, loaded }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) {
    throw new Error("useSiteConfig must be used within SiteConfigProvider");
  }
  return ctx;
}
